import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';
import { v4 as uuidv4 } from 'uuid';

import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { Boton } from '#components/mobile/ui/Boton';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { useEstadoDeVista } from '#components/mobile/ui/estadoDeVista';
import { Icono } from '#components/mobile/ui/Icono';
import {
  color,
  densidad,
  espacio,
  radio,
  sombra,
  texto,
} from '#components/mobile/ui/tokens';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { KEYPAD_Z_INDEX } from './AssignKeypad';
import { DURACION_PAGADA } from './CheckPagada';
import {
  alternarCategoria,
  borrarFiltro,
  contarPorEstado,
  escribirGuardados,
  filtrarGrupos,
  guardarFiltro,
  hayFiltro,
  leerGuardados,
  seVe,
  SIN_FILTRO,
} from './filtrosCategoriasLogica';
import type {
  ContextoFiltro,
  EjesFiltro,
  EstadoFiltro,
  FiltroGuardado,
  FiltroPropio,
} from './filtrosCategoriasLogica';
import { cumpleFiltro } from './objetivos';
import type { DatosCategoriaMes } from './objetivos';
import { pagadasDelMes } from './pagadas';
import { useCuentasCategorias } from './useCuentasCategorias';
import { guardarVistas, leerVistas, usePagadas } from './usePagadas';

type GrupoConCategorias = CategoryGroupEntity & {
  categories: CategoryEntity[];
};

export function useNombresEstado(): Record<EstadoFiltro, string> {
  const { t } = useTranslation();
  return {
    ninguno: t('All'),
    infrafinanciadas: t('Underfunded'),
    sobrefinanciadas: t('Overfunded'),
    'gastado-de-mas': t('Overspent'),
    ignoradas: t('Enough this month'),
    pagadas: t('Paid categories'),
    'sin-objetivo': t('No target'),
  };
}

type FiltrosCategoriasArgs<G extends { categories: { id: string }[] }> = {
  grupos: readonly G[];
  /** Mes visto (las pagadas son de ese mes). */
  month: string;
  datos: ReadonlyMap<string, DatosCategoriaMes>;
  /** Se llama al cambiar de filtro (p. ej. para cerrar el teclado). */
  onCambio?: () => void;
};

/**
 * Estado de los filtros del Plan: tres ejes que se cruzan (cuenta, dinero y
 * gasto), sin recordar entre visitas; filtros guardados con nombre en la pref
 * sincronizada `asignar-filtros`; cuántas salen con cada opción de dinero y
 * los grupos ya filtrados. Lo pagado no se esconde nunca: lleva su ✓.
 */
export function useFiltrosCategorias<
  G extends { categories: { id: string }[] },
>({ grupos, month, datos, onCambio }: FiltrosCategoriasArgs<G>) {
  const { fijos, manuales, setFijos } = usePagadas(month);
  const pagadas = useMemo(
    () => pagadasDelMes({ datos, fijos, manuales }),
    [datos, fijos, manuales],
  );
  // Recién pagadas (la primera vez que se ven en este mes): el ✓ se estampa.
  const [recientes, setRecientes] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    const vistas = leerVistas(month);
    const nuevas = [...pagadas].filter(id => !vistas.has(id));
    // Las que dejan de estar pagadas vuelven a poder animarse.
    const sigue = [...vistas].filter(id => pagadas.has(id));
    guardarVistas(month, [...sigue, ...nuevas]);
    if (nuevas.length === 0) {
      return;
    }
    setRecientes(prev => new Set([...prev, ...nuevas]));
    window.setTimeout(() => {
      setRecientes(prev => {
        const n = new Set(prev);
        nuevas.forEach(id => n.delete(id));
        return n;
      });
    }, DURACION_PAGADA);
  }, [pagadas, month]);
  const [editandoFijos, setEditandoFijos] = useState(false);

  // Cuentas: con una sola no hay nada que separar y el banderín no sale.
  const cuentasCat = useCuentasCategorias(month);
  const cuentas = useMemo(
    () => (cuentasCat.cuentas.length > 1 ? cuentasCat.cuentas : []),
    [cuentasCat.cuentas],
  );

  const [raw, setRaw] = useSyncedPref('asignar-filtros');
  const guardados = useMemo(() => leerGuardados(raw), [raw]);
  const [ejesPuestos, setEjesPuestos] = useEstadoDeVista<EjesFiltro>(
    'filtro-plan',
    SIN_FILTRO,
  );
  // Una cuenta cerrada (o de otro presupuesto) vuelve a «todas».
  const ejes = useMemo<EjesFiltro>(
    () =>
      ejesPuestos.cuenta != null &&
      !cuentas.some(c => c.id === ejesPuestos.cuenta)
        ? { ...ejesPuestos, cuenta: null }
        : ejesPuestos,
    [ejesPuestos, cuentas],
  );
  const setEjes = useCallback(
    (nuevos: EjesFiltro) => {
      onCambio?.();
      setEjesPuestos(nuevos);
    },
    [onCambio, setEjesPuestos],
  );

  const { cuentaDe } = cuentasCat;
  const ctx = useMemo<ContextoFiltro>(
    () => ({
      datos,
      pagadas,
      fijos,
      cuentaDe,
      cumpleDinero: (f, d) => cumpleFiltro(f, d),
    }),
    [datos, pagadas, fijos, cuentaDe],
  );
  const ids = useMemo(
    () => grupos.flatMap(g => g.categories.map(c => c.id)),
    [grupos],
  );
  const conteos = useMemo(
    () => contarPorEstado(ids, ejes, ctx),
    [ids, ejes, ctx],
  );
  const filtrando = hayFiltro(ejes);
  const gruposFiltrados = useMemo(
    () =>
      filtrando
        ? filtrarGrupos(grupos, c => seVe(c.id, ejes, ctx))
        : [...grupos],
    [filtrando, grupos, ejes, ctx],
  );

  const guardar = (nombre: string) => {
    const filtro: FiltroGuardado = { ...ejes, id: uuidv4(), nombre };
    setRaw(escribirGuardados(guardarFiltro(guardados, filtro)));
  };
  const borrar = (id: string) => {
    setRaw(escribirGuardados(borrarFiltro(guardados, id)));
  };
  // Cuántas salen con un filtro guardado (para la lista de guardados).
  const cuantasCon = (f: EjesFiltro) =>
    ids.filter(id => seVe(id, f, ctx)).length;

  return {
    ejes,
    setEjes,
    /** Algún eje puesto. */
    filtrando,
    cuentas,
    guardados,
    guardar,
    borrar,
    cuantasCon,
    conteos,
    gruposFiltrados,
    pagadas,
    recientes,
    fijos,
    setFijos,
    editandoFijos,
    setEditandoFijos,
  };
}

type EditorFiltroProps = {
  /** `null` para uno nuevo. */
  filtro: FiltroPropio | null;
  /** Sin nombre (p. ej. «Pagos fijos»): solo las casillas, con este título. */
  titulo?: string;
  grupos: GrupoConCategorias[];
  onGuardar: (filtro: FiltroPropio) => void;
  onBorrar?: (id: string) => void;
  onCerrar: () => void;
};

/**
 * Hoja para crear o editar un filtro propio: nombre y casillas de
 * categorías (tocar, tocar, tocar), Guardar y, si ya existe, Borrar.
 */
export function EditorFiltro({
  filtro,
  titulo,
  grupos,
  onGuardar,
  onBorrar,
  onCerrar,
}: EditorFiltroProps) {
  const { t } = useTranslation();
  // «Atrás» del móvil cierra el editor (no cambia de pestaña).
  useCerrarConAtras(true, onCerrar);
  const [nombre, setNombre] = useState(filtro?.nombre ?? '');
  const [seleccion, setSeleccion] = useState<string[]>(
    filtro?.categorias ?? [],
  );
  const [busqueda, setBusqueda] = useState('');
  // Ocultas (o de un grupo oculto): nunca se ofrecen.
  const visibles = useMemo(
    () =>
      grupos
        .filter(g => !g.hidden)
        .map(g => ({
          ...g,
          categories: g.categories.filter(c => !c.hidden),
        }))
        .filter(g => g.categories.length > 0),
    [grupos],
  );
  const mostrados = useMemo(() => {
    const q = normalizar(busqueda.trim());
    if (!q) {
      return visibles;
    }
    return visibles
      .map(g =>
        normalizar(g.name).includes(q)
          ? g
          : {
              ...g,
              categories: g.categories.filter(c =>
                normalizar(c.name).includes(q),
              ),
            },
      )
      .filter(g => g.categories.length > 0);
  }, [visibles, busqueda]);
  // Al guardar solo cuentan las que se ven (una oculta elegida antes, fuera).
  const idsVisibles = useMemo(
    () => new Set(visibles.flatMap(g => g.categories.map(c => c.id))),
    [visibles],
  );
  const elegidas = seleccion.filter(id => idsVisibles.has(id));
  const puedeGuardar =
    titulo != null || (nombre.trim() !== '' && elegidas.length > 0);

  return createPortal(
    <View
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: KEYPAD_Z_INDEX + 5,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        justifyContent: 'flex-end',
      }}
      onClick={e => {
        if (e.target === e.currentTarget) {
          onCerrar();
        }
      }}
    >
      <View
        role="dialog"
        aria-modal
        aria-label={titulo ?? (filtro ? t('Edit filter') : t('New filter'))}
        data-testid="editor-filtro"
        style={{
          maxHeight: '88%',
          backgroundColor: color.surface,
          borderRadius: `${radio.heroe}px ${radio.heroe}px 0 0`,
          boxShadow: sombra.hoja,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <View
          style={{
            flexShrink: 0,
            gap: 10,
            padding: `${espacio.margen}px ${espacio.margen}px 8px`,
          }}
        >
          <Text style={{ ...texto.titulo, color: color.fg }}>
            {titulo ??
              (filtro ? <Trans>Edit filter</Trans> : <Trans>New filter</Trans>)}
          </Text>
          {titulo == null && (
            <Input
              autoFocus={!filtro}
              value={nombre}
              placeholder={t('Name (e.g. Annual bills)')}
              aria-label={t('Filter name')}
              data-testid="filtro-nombre"
              onChangeValue={setNombre}
              style={{
                height: 44,
                fontSize: 15,
                borderRadius: radio.sm,
                backgroundColor: color.surface2,
                color: color.fg,
                border: `1px solid ${color.line}`,
                padding: '0 12px',
              }}
            />
          )}
          <Text style={{ ...texto.etiqueta, color: color.fg3 }}>
            <Trans>Categories</Trans> · {elegidas.length}
          </Text>
          <Input
            value={busqueda}
            placeholder={t('Search')}
            aria-label={t('Search categories')}
            data-testid="selector-buscar"
            onChangeValue={setBusqueda}
            style={{
              height: 36,
              fontSize: 14,
              borderRadius: radio.sm,
              backgroundColor: color.surface2,
              color: color.fg,
              border: `1px solid ${color.line}`,
              padding: '0 10px',
            }}
          />
        </View>
        <View style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {mostrados.length === 0 && (
            <Text
              style={{
                ...densidad.nombre,
                color: color.fg3,
                padding: espacio.margen,
              }}
            >
              <Trans>No categories match</Trans>
            </Text>
          )}
          {mostrados.map(grupo => (
            <View key={grupo.id} style={{ flexShrink: 0 }}>
              <Text
                style={{
                  ...densidad.grupo,
                  fontSize: 13,
                  color: color.fg3,
                  padding: `8px ${espacio.margen}px 4px`,
                  backgroundColor: color.surface2,
                }}
              >
                {grupo.name}
              </Text>
              {grupo.categories.map(c => {
                const marcada = seleccion.includes(c.id);
                const { emoji, resto } = separarEmoji(c.name);
                return (
                  <Button
                    key={c.id}
                    variant="bare"
                    aria-pressed={marcada}
                    data-testid="filtro-categoria"
                    onPress={() =>
                      setSeleccion(s => alternarCategoria(s, c.id))
                    }
                    style={{
                      display: 'flex',
                      flexShrink: 0,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 10,
                      minHeight: 44,
                      padding: `0 ${espacio.margen}px`,
                      borderRadius: 0,
                      textAlign: 'left',
                      color: color.fg,
                      boxShadow: `inset 0 -1px 0 ${color.line}`,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: marcada ? color.accent : 'transparent',
                        border: marcada ? 0 : `2px solid ${color.line2}`,
                        color: color.accentInk,
                      }}
                    >
                      {marcada && <Icono nombre="check" size={14} />}
                    </span>
                    <Text style={{ ...densidad.nombre, color: color.fg }}>
                      {emoji ? `${emoji} ${resto}` : c.name}
                    </Text>
                  </Button>
                );
              })}
            </View>
          ))}
        </View>
        <View
          style={{
            flexShrink: 0,
            flexDirection: 'row',
            gap: 8,
            padding: espacio.margen,
            borderTop: `1px solid ${color.line}`,
          }}
        >
          {filtro && onBorrar && titulo == null && (
            <Boton
              variante="tonal"
              onPress={() => onBorrar(filtro.id)}
              data-testid="filtro-borrar"
            >
              <Trans>Delete</Trans>
            </Boton>
          )}
          <View style={{ flex: 1 }} />
          <Boton variante="fantasma" onPress={onCerrar}>
            <Trans>Cancel</Trans>
          </Boton>
          <Boton
            isDisabled={!puedeGuardar}
            data-testid="filtro-guardar"
            onPress={() =>
              onGuardar({
                id: filtro?.id ?? uuidv4(),
                nombre,
                categorias: elegidas,
              })
            }
          >
            <Trans>Save</Trans>
          </Boton>
        </View>
      </View>
    </View>,
    document.body,
  );
}

/** Sin mayúsculas ni tildes, para buscar. */
function normalizar(s: string) {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

type SelectorCategoriasProps = {
  titulo: string;
  grupos: readonly CategoryGroupEntity[];
  /** Las ya elegidas (salen marcadas). */
  seleccion: readonly string[];
  /** La selección completa, de una vez. */
  onGuardar: (ids: string[]) => void;
  onCerrar: () => void;
};

/**
 * Hoja de selección múltiple de categorías (la del editor de filtros):
 * buscador, grupos, casillas con las ya elegidas marcadas y Guardar /
 * Cancelar. Sin ocultas.
 */
export function SelectorCategorias({
  titulo,
  grupos,
  seleccion,
  onGuardar,
  onCerrar,
}: SelectorCategoriasProps) {
  return (
    <EditorFiltro
      titulo={titulo}
      filtro={{ id: 'selector', nombre: '', categorias: [...seleccion] }}
      grupos={grupos.map(g => ({ ...g, categories: g.categories ?? [] }))}
      onGuardar={f => onGuardar(f.categorias)}
      onCerrar={onCerrar}
    />
  );
}
